import { roundedRectPath, shapePath } from '../model/shapes';
import { layoutTextLayer, nativeLetterSpacing } from '../model/textLayout';
import { gradientPoints, stopColor, withAlpha } from '../model/colors';
import { framingOf, placement } from '../model/framing';
import { iconSource, readyImage } from './resources';
import { drawWithEffects, visibleEffects } from './effects';
import { drawPlaceholder } from './placeholder';
import { readyStill } from './video';
import { isPlaceholderSource } from '../model/schema';
import { isActive, sourceTime } from '../model/timeline';

// The one painter. The stage, every thumbnail and the export call `drawPage`, so a saved
// composition downloads exactly as it was shown. It paints synchronously with what is
// loaded: an image still loading is a neutral placeholder on screen (the export waits for
// it first, see exportArt).
// A color or a gradient as a canvas fill style over a box (a radial gradient reaches the
// box's corners from its center).
function paint(ctx, fill, width, height) {
  if (!fill || fill.type === 'image') return 'transparent';
  if (fill.type === 'solid') return fill.opacity !== undefined && fill.opacity < 1 ? withAlpha(fill.color, fill.opacity) : fill.color;
  let gradient;
  if (fill.type === 'radial') gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.hypot(width, height) / 2);
  else {
    const { x0, y0, x1, y1 } = gradientPoints(fill.angle, width, height);
    gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  }
  fill.stops.forEach(stop => gradient.addColorStop(stop.offset, stopColor(stop)));
  return gradient;
}

const supportsFilter = ctx => typeof ctx.filter === 'string';
function filterString(adjust, scale) {
  if (!adjust) return '';
  const parts = [];
  if (adjust.brightness) parts.push(`brightness(${1 + adjust.brightness / 100})`);
  if (adjust.contrast) parts.push(`contrast(${1 + adjust.contrast / 100})`);
  if (adjust.saturation) parts.push(`saturate(${1 + adjust.saturation / 100})`);
  if (adjust.grayscale) parts.push('grayscale(1)');
  if (adjust.blur) parts.push(`blur(${adjust.blur * scale}px)`);
  return parts.join(' ');
}

// Where the canvas has no `filter` (Safari), the color adjustments are applied per pixel on
// a cached offscreen copy; blur is left out there.
const adjusted = new Map();
function adjustedImage(image, adjust, width, height) {
  const w = Math.max(1, Math.min(2048, Math.round(width)));
  const h = Math.max(1, Math.min(2048, Math.round(height)));
  const key = `${image.src}|${adjust.brightness}|${adjust.contrast}|${adjust.saturation}|${adjust.grayscale}|${w}x${h}`;
  if (adjusted.has(key)) return adjusted.get(key);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  const pixels = data.data;
  const b = 1 + adjust.brightness / 100;
  const c = 1 + adjust.contrast / 100;
  const s = adjust.grayscale ? 0 : 1 + adjust.saturation / 100;
  for (let i = 0; i < pixels.length; i += 4) {
    let r = pixels[i] * b;
    let g = pixels[i + 1] * b;
    let bl = pixels[i + 2] * b;
    r = (r - 127.5) * c + 127.5;
    g = (g - 127.5) * c + 127.5;
    bl = (bl - 127.5) * c + 127.5;
    const gray = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
    pixels[i] = gray + (r - gray) * s;
    pixels[i + 1] = gray + (g - gray) * s;
    pixels[i + 2] = gray + (bl - gray) * s;
  }
  ctx.putImageData(data, 0, 0);
  if (adjusted.size > 24) adjusted.delete(adjusted.keys().next().value);
  adjusted.set(key, canvas);
  return canvas;
}

// `part` paints only the background box ('surface') or only the letters ('ink').
function drawText(ctx, layer, scale, placeholders, part) {
  const layout = layoutTextLayer(layer);
  const { style } = layer;
  const highlight = style.highlight;
  if (highlight && part !== 'ink') {
    ctx.fillStyle = highlight.color;
    if (highlight.mode === 'box') ctx.fill(new Path2D(roundedRectPath(layer.width, layout.height, highlight.radius)));
    else layout.lines.forEach(line => {
      if (!line.text) return;
      const path = roundedRectPath(line.width + highlight.padding * 2, layout.lineHeight, highlight.radius);
      ctx.save();
      ctx.translate(line.x - highlight.padding, line.top);
      ctx.fill(new Path2D(path));
      ctx.restore();
    });
  }
  if (part === 'surface') return;
  ctx.font = layout.font;
  ctx.fillStyle = textPaint(ctx, layer, layout.height, scale, placeholders);
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const native = nativeLetterSpacing(ctx);
  if (native) ctx.letterSpacing = `${layout.spacing}px`;
  layout.lines.forEach(line => {
    if (!line.text) return;
    if (native || !layout.spacing) ctx.fillText(line.text, line.x, line.baseline);
    else {
      let x = line.x;
      for (const char of line.text) { ctx.fillText(char, x, line.baseline); x += ctx.measureText(char).width + layout.spacing; }
    }
    if (style.underline) ctx.fillRect(line.x, line.baseline + layout.size * 0.1, Math.max(0, line.width - layout.spacing), Math.max(1, layout.size * 0.055));
  });
}

const PLACEHOLDER = 'rgba(128, 128, 128, 0.22)';
// Where an image lands in a box: `cover` fills it (cropping), `contain` fits it whole;
// zoom enlarges from there and focus picks which part stays in view (model/framing).
const frame = (image, width, height, framing) => placement(width, height, image.naturalWidth, image.naturalHeight, framing);

// A photo paint (shape or text) rendered once at the box's device size: framed and
// adjusted, so a mask (the shape's outline, the text's letters) only has to cut it.
const photoCanvases = new Map();
function photoCanvas(image, fill, width, height, scale) {
  const w = Math.max(1, Math.min(4096, Math.round(width * scale)));
  const h = Math.max(1, Math.min(4096, Math.round(height * scale)));
  const a = fill.adjust || {};
  const key = `${image.src}|${fill.fit}|${fill.zoom}|${fill.focus_x}|${fill.focus_y}|${a.brightness}|${a.contrast}|${a.saturation}|${a.blur}|${a.grayscale}|${w}x${h}`;
  if (photoCanvases.has(key)) return photoCanvases.get(key);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const { dx, dy, dw, dh } = frame(image, w, h, fill);
  ctx.imageSmoothingQuality = 'high';
  const filter = filterString(fill.adjust, scale);
  if (filter && supportsFilter(ctx)) { ctx.filter = filter; ctx.drawImage(image, dx, dy, dw, dh); }
  else if (filter) ctx.drawImage(adjustedImage(image, fill.adjust, dw, dh), dx, dy, dw, dh);
  else ctx.drawImage(image, dx, dy, dw, dh);
  if (photoCanvases.size > 16) photoCanvases.delete(photoCanvases.keys().next().value);
  photoCanvases.set(key, canvas);
  return canvas;
}

// A shape filled with a photo: the photo framed in the shape's box, clipped to its outline.
function drawImageFill(ctx, layer, path, scale, placeholders) {
  const empty = isPlaceholderSource(layer.fill.source);
  const image = empty ? null : readyImage(layer.fill.source?.url);
  const canvas = image?.naturalWidth ? photoCanvas(image, layer.fill, layer.width, layer.height, scale) : null;
  ctx.save();
  ctx.clip(path);
  if (empty) drawPlaceholder(ctx, layer.width, layer.height);
  else if (canvas) ctx.drawImage(canvas, 0, 0, layer.width, layer.height);
  else if (placeholders) { ctx.fillStyle = PLACEHOLDER; ctx.fillRect(0, 0, layer.width, layer.height); }
  ctx.restore();
}

// What paints a text's letters: its color, a gradient over its box, or a photo seen through
// them (a pattern of the framed photo; its color while the photo loads).
function textPaint(ctx, layer, height, scale, placeholders) {
  const fill = layer.style.fill;
  if (!fill) return layer.style.color;
  if (fill.type !== 'image') return paint(ctx, fill, layer.width, height);
  if (isPlaceholderSource(fill.source)) return layer.style.color;
  const image = readyImage(fill.source?.url);
  const canvas = image?.naturalWidth ? photoCanvas(image, fill, layer.width, height, scale) : null;
  const pattern = canvas && typeof ctx.createPattern === 'function' ? ctx.createPattern(canvas, 'no-repeat') : null;
  if (!pattern || typeof pattern.setTransform !== 'function' || typeof DOMMatrix === 'undefined') return placeholders ? layer.style.color : 'transparent';
  pattern.setTransform(new DOMMatrix([1 / scale, 0, 0, 1 / scale, 0, 0]));
  return pattern;
}

// The pixel size of what a video layer paints: a <video> or a grabbed frame (a canvas).
const frameSize = source => (source ? { width: source.videoWidth || source.width || 0, height: source.videoHeight || source.height || 0 } : { width: 0, height: 0 });

// The move mode's preview: the whole photo (a video's frame), faded, where it reaches past its mask.
function drawReveal(ctx, layer, height, videoFrame) {
  const framing = framingOf(layer);
  const video = layer.type === 'video';
  const image = video ? videoFrame : framing && readyImage(framing.source?.url);
  const size = video ? frameSize(image) : { width: image?.naturalWidth, height: image?.naturalHeight };
  if (!image || !size.width) return;
  const { dx, dy, dw, dh } = placement(layer.width, height, size.width, size.height, framing);
  const flips = layer.type === 'image' || video;
  ctx.save();
  ctx.globalAlpha *= 0.32;
  if (flips && (layer.flip_x || layer.flip_y)) {
    ctx.translate(layer.width / 2, height / 2);
    ctx.scale(layer.flip_x ? -1 : 1, layer.flip_y ? -1 : 1);
    ctx.translate(-layer.width / 2, -height / 2);
  }
  ctx.drawImage(image, flips && layer.flip_x ? layer.width - dx - dw : dx, flips && layer.flip_y ? height - dy - dh : dy, dw, dh);
  ctx.restore();
}

function drawShape(ctx, layer, scale, placeholders) {
  const stroke = layer.stroke && layer.stroke.width > 0 ? layer.stroke : null;
  const path = new Path2D(shapePath(layer.shape, layer.width, layer.height, { cornerRadius: layer.corner_radius, strokeWidth: stroke ? stroke.width : 0 }));
  if (layer.fill?.type === 'image' && layer.shape !== 'line') drawImageFill(ctx, layer, path, scale, placeholders);
  else if (layer.fill && layer.shape !== 'line') {
    ctx.fillStyle = paint(ctx, layer.fill, layer.width, layer.height);
    ctx.fill(path);
  }
  if (stroke) {
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke(path);
  }
}

function drawImage(ctx, layer, height, scale, placeholders) {
  const width = layer.width;
  ctx.save();
  ctx.clip(new Path2D(roundedRectPath(width, height, layer.corner_radius)));
  if (isPlaceholderSource(layer.source)) {
    drawPlaceholder(ctx, width, height, { avatar: layer.binding === 'avatar' });
    ctx.restore();
    return;
  }
  const image = readyImage(layer.source?.url);
  if (!image || !image.naturalWidth) {
    if (placeholders) { ctx.fillStyle = PLACEHOLDER; ctx.fillRect(0, 0, width, height); }
    ctx.restore();
    return;
  }
  const placed = frame(image, width, height, layer);
  const { dw, dh } = placed;
  let { dx, dy } = placed;
  // A flip mirrors the same crop in place.
  if (layer.flip_x || layer.flip_y) {
    ctx.translate(width / 2, height / 2);
    ctx.scale(layer.flip_x ? -1 : 1, layer.flip_y ? -1 : 1);
    ctx.translate(-width / 2, -height / 2);
    if (layer.flip_x) dx = width - dx - dw;
    if (layer.flip_y) dy = height - dy - dh;
  }
  ctx.imageSmoothingQuality = 'high';
  const filter = filterString(layer.adjust, scale);
  if (filter && supportsFilter(ctx)) {
    ctx.filter = filter;
    ctx.drawImage(image, dx, dy, dw, dh);
    ctx.filter = 'none';
  } else if (filter) ctx.drawImage(adjustedImage(image, layer.adjust, dw * scale, dh * scale), dx, dy, dw, dh);
  else ctx.drawImage(image, dx, dy, dw, dh);
  ctx.restore();
}

// A video's frame (2.13.0), framed like an image in its box, cut by its mask. Color
// adjustments follow the canvas `filter` where there is one (a per-pixel copy of every frame
// would be too slow, so Safari plays them unadjusted).
function drawVideo(ctx, layer, height, scale, placeholders, source) {
  const width = layer.width;
  ctx.save();
  ctx.clip(new Path2D(shapePath(layer.mask || 'rect', width, height, { cornerRadius: layer.corner_radius })));
  const size = frameSize(source);
  if (!size.width) {
    if (placeholders) { ctx.fillStyle = PLACEHOLDER; ctx.fillRect(0, 0, width, height); }
    ctx.restore();
    return;
  }
  const placed = placement(width, height, size.width, size.height, layer);
  const { dw, dh } = placed;
  let { dx, dy } = placed;
  if (layer.flip_x || layer.flip_y) {
    ctx.translate(width / 2, height / 2);
    ctx.scale(layer.flip_x ? -1 : 1, layer.flip_y ? -1 : 1);
    ctx.translate(-width / 2, -height / 2);
    if (layer.flip_x) dx = width - dx - dw;
    if (layer.flip_y) dy = height - dy - dh;
  }
  ctx.imageSmoothingQuality = 'high';
  const filter = filterString(layer.adjust, scale);
  if (filter && supportsFilter(ctx)) ctx.filter = filter;
  ctx.drawImage(source, dx, dy, dw, dh);
  ctx.restore();
}

// What a video layer paints at page `time` when no surface hands a frame of its own: the
// still of its file at that second (thumbnails, the chat cards, a PNG of the page).
export const stillFrame = (layer, time = 0) => readyStill(layer.source?.url, sourceTime(layer, time));

function drawIcon(ctx, layer, scale) {
  const image = readyImage(iconSource(layer, scale));
  if (!image) return;
  const side = Math.min(layer.width, layer.height);
  ctx.drawImage(image, (layer.width - side) / 2, (layer.height - side) / 2, side, side);
}

// `frame` is what a video layer paints (a <video> or a canvas; its still when none).
export function drawLayer(ctx, layer, { scale = 1, placeholders = true, reveal = false, frame: videoFrame = null } = {}) {
  const height = layer.type === 'text' ? layoutTextLayer(layer).height : layer.height;
  const source = layer.type === 'video' ? videoFrame || stillFrame(layer) : null;
  ctx.save();
  ctx.globalAlpha *= layer.opacity;
  ctx.translate(layer.x + layer.width / 2, layer.y + height / 2);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.translate(-layer.width / 2, -height / 2);
  if (reveal) drawReveal(ctx, layer, height, source);
  const paintBody = (target, at, part) => {
    if (layer.type === 'text') drawText(target, layer, at, placeholders, part);
    else if (layer.type === 'shape') drawShape(target, layer, at, placeholders);
    else if (layer.type === 'image') drawImage(target, layer, height, at, placeholders);
    else if (layer.type === 'video') drawVideo(target, layer, height, at, placeholders, source);
    else if (layer.type === 'icon') drawIcon(target, layer, at);
  };
  const effects = visibleEffects(layer);
  if (effects.length) drawWithEffects(ctx, layer, height, effects, scale, paintBody, { layered: layer.type === 'text' && !!layer.style.highlight });
  else paintBody(ctx, scale);
  ctx.restore();
}

// Paints one page at `scale` (device pixels per document pixel). `skip` leaves out layers
// another surface shows meanwhile (the text being edited in place); `reveal` is the layer
// whose photo is being moved inside its mask (the stage shows the rest of the photo faded).
// 2.13.0: the page as it shows at `time` (seconds on its timeline; a clip shows only while it
// plays); `frames(layer)` hands the frame a video layer paints (the stage's players, the
// export's decoded frames), else its still at that time.
export function drawPage(ctx, doc, page, { scale = 1, skip = null, placeholders = true, override = null, reveal = null, time = 0, frames = null } = {}) {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.scale(scale, scale);
  ctx.fillStyle = paint(ctx, page.background, doc.width, doc.height);
  ctx.fillRect(0, 0, doc.width, doc.height);
  page.layers.forEach(source => {
    const layer = override?.[source.id] || source;
    if (!layer.visible || skip?.has(layer.id) || !isActive(layer, time)) return;
    const frame = layer.type === 'video' ? frames?.(layer) || stillFrame(layer, time) : null;
    drawLayer(ctx, layer, { scale, placeholders, reveal: layer.id === reveal, frame });
  });
  ctx.restore();
}
